// ============================================
// Sidebar Component
// Main navigation sidebar for Store Admin
// ============================================

import { NavLink, useLocation } from 'react-router-dom';
import { ADMIN_ROUTES } from '@conai/shared-constants';
import { cn } from '../../utils/cn';
import {
  DashboardIcon,
  StoresIcon,
  ProductsIcon,
  CategoriesIcon,
  SuppliersIcon,
  InventoryIcon,
  StockImportsIcon,
  OrdersIcon,
  PaymentsIcon,
  CustomersIcon,
  LoyaltyIcon,
  AccessControlIcon,
  DoorIcon,
  FeedbackIcon,
  UsersIcon,
  PromotionsIcon,
  ReportsIcon,
  AuditIcon,
  SettingsIcon,
  LogoutIcon,
  XIcon,
  // Năm icon thêm 19/09/2026 để gỡ trùng icon ở nav — xem ghi chú tại
  // `components/icons/index.tsx`.
  IdCardIcon,
  AgeGateIcon,
  DisputeIcon,
  ReturnIcon,
  DailyCloseIcon,
} from '../icons';
import { useAuthStore } from '../../stores/auth.store';
import { useI18n } from '../../i18n';
import { translateOrFallback } from '../../utils/apiError';
import { useLockBodyScroll } from '../../hooks/useLockBodyScroll';

// ============================================
// Types
// ============================================

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  labelKey:
    | 'navDashboard'
    | 'navStores'
    | 'navProducts'
    | 'navCategories'
    | 'navSuppliers'
    | 'navInventory'
    | 'navStockImports'
    | 'navOrders'
    | 'navPayments'
    | 'navPromotions'
    | 'navCustomers'
    | 'navAgeVerifications'
    | 'navRestrictedSales'
    | 'navFeedback'
    | 'navDisputes'
    | 'navReturns'
    | 'navLoyalty'
    | 'navAccessControl'
    | 'navAtWork'
    | 'navUsers'
    | 'navReports'
    | 'navDailyClose'
    | 'navAuditLogs'
    | 'navSettings';
  path: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  /**
   * Câu dự phòng khi key CHƯA được merge vào `i18n/locales/*.json`.
   *
   * `t()` trả về CHÍNH KEY khi thiếu bản dịch, nên một mục mới sẽ hiện chữ
   * "navFeedback" ngay trên thanh điều hướng. Mục nào đã có bản dịch thì bỏ
   * trống trường này.
   */
  fallbackLabel?: string;
}

// ============================================
// Navigation Items
// ============================================

const mainNavItems: NavItem[] = [
  { labelKey: 'navDashboard', path: ADMIN_ROUTES.DASHBOARD, icon: DashboardIcon },
  { labelKey: 'navStores', path: ADMIN_ROUTES.STORES, icon: StoresIcon },
  { labelKey: 'navProducts', path: ADMIN_ROUTES.PRODUCTS, icon: ProductsIcon },
  { labelKey: 'navCategories', path: ADMIN_ROUTES.CATEGORIES, icon: CategoriesIcon },
  { labelKey: 'navSuppliers', path: ADMIN_ROUTES.SUPPLIERS, icon: SuppliersIcon },
];

const inventoryNavItems: NavItem[] = [
  { labelKey: 'navInventory', path: ADMIN_ROUTES.INVENTORY, icon: InventoryIcon },
  { labelKey: 'navStockImports', path: ADMIN_ROUTES.STOCK_IMPORTS, icon: StockImportsIcon },
];

const salesNavItems: NavItem[] = [
  { labelKey: 'navOrders', path: ADMIN_ROUTES.ORDERS, icon: OrdersIcon },
  { labelKey: 'navPayments', path: ADMIN_ROUTES.PAYMENTS, icon: PaymentsIcon },
  { labelKey: 'navPromotions', path: ADMIN_ROUTES.PROMOTIONS, icon: PromotionsIcon },
];

const customerNavItems: NavItem[] = [
  { labelKey: 'navCustomers', path: ADMIN_ROUTES.CUSTOMERS, icon: CustomersIcon },
  // Duyệt xác minh 18+ — chặn/mở bán rượu & thuốc lá cho từng khách
  { labelKey: 'navAgeVerifications', path: ADMIN_ROUTES.AGE_VERIFICATIONS, icon: IdCardIcon },
  // Sổ bằng chứng bán hàng 18+ — đặt ngay cạnh màn duyệt vì cùng một việc:
  // chứng minh cửa hàng bán đúng luật khi bị thanh tra hỏi.
  { labelKey: 'navRestrictedSales', path: ADMIN_ROUTES.RESTRICTED_SALES, icon: AgeGateIcon },
  // Góp ý của khách (bug #208) — cùng nhóm "Khách hàng" vì đây cũng là việc đọc
  // và trả lời một việc do khách khởi xướng.
  {
    labelKey: 'navFeedback',
    path: ADMIN_ROUTES.FEEDBACK,
    icon: FeedbackIcon,
    fallbackLabel: 'Feedback',
  },
  // Khiếu nại hoá đơn — cùng nhóm "việc do khách khởi xướng" với góp ý, nhưng
  // chốt ở đây là hoàn tiền thật (sheet 2 #58/#60).
  {
    labelKey: 'navDisputes',
    path: ADMIN_ROUTES.DISPUTES,
    icon: DisputeIcon,
    fallbackLabel: 'Disputes',
  },
  // Đổi/trả tại quầy — nhân viên quầy dùng hằng ngày (sheet 2 #55/#56/#59).
  {
    labelKey: 'navReturns',
    path: ADMIN_ROUTES.RETURNS,
    icon: ReturnIcon,
    fallbackLabel: 'Returns',
  },
  { labelKey: 'navLoyalty', path: ADMIN_ROUTES.LOYALTY, icon: LoyaltyIcon },
  { labelKey: 'navAccessControl', path: ADMIN_ROUTES.ACCESS_CONTROL, icon: AccessControlIcon },
  { labelKey: 'navAtWork', path: ADMIN_ROUTES.ATWORK, icon: DoorIcon },
];

const managementNavItems: NavItem[] = [
  { labelKey: 'navUsers', path: ADMIN_ROUTES.USERS, icon: UsersIcon },
  { labelKey: 'navReports', path: ADMIN_ROUTES.REPORTS, icon: ReportsIcon },
  // Chốt cuối ngày (mục #53) — mục RIÊNG chứ không nhét vào trang Reports:
  // đây là việc làm hằng ngày lúc đóng cửa, cắt ngày theo múi giờ cửa hàng, khác
  // hẳn báo cáo phân tích theo khoảng ngày ở trên.
  { labelKey: 'navDailyClose', path: ADMIN_ROUTES.REPORTS_DAILY_CLOSE, icon: DailyCloseIcon },
  { labelKey: 'navAuditLogs', path: ADMIN_ROUTES.AUDIT_LOGS, icon: AuditIcon },
];

const bottomNavItems: NavItem[] = [
  { labelKey: 'navSettings', path: ADMIN_ROUTES.SETTINGS, icon: SettingsIcon },
];

/** Mọi đường dẫn có trên thanh điều hướng — dùng để chọn mục khớp SÂU NHẤT. */
const allNavPaths: string[] = [
  ...mainNavItems,
  ...inventoryNavItems,
  ...salesNavItems,
  ...customerNavItems,
  ...managementNavItems,
  ...bottomNavItems,
].map((item) => item.path);

/** Đường dẫn hiện tại có nằm trong nhánh của `navPath` không (khớp theo SEGMENT). */
function isWithin(pathname: string, navPath: string): boolean {
  if (navPath === '/') return pathname === '/';
  return pathname === navPath || pathname.startsWith(`${navPath}/`);
}

// ============================================
// Component
// ============================================

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation();
  const logout = useAuthStore((state) => state.logout);
  const { t } = useI18n();

  // Khoá scroll nền khi menu mobile đang mở (bug 126)
  useLockBodyScroll(isOpen);

  /** Nhãn của một mục: dịch được thì dịch, chưa có bản dịch thì dùng câu dự phòng. */
  const navLabel = (item: NavItem) =>
    item.fallbackLabel ? translateOrFallback(t, item.labelKey, item.fallbackLabel) : t(item.labelKey);

  /**
   * Sáng mục khớp SÂU NHẤT, không phải mọi mục có tiền tố trùng.
   *
   * `/reports/daily-close` là nhánh con của `/reports`, nên so tiền tệ thô sẽ
   * sáng cả hai mục cùng lúc. So theo segment rồi lấy đường dẫn dài nhất khớp
   * được: mở Daily Close thì chỉ Daily Close sáng.
   */
  const isActiveRoute = (path: string) => {
    if (!isWithin(location.pathname, path)) return false;
    const deepestMatch = allNavPaths
      .filter((navPath) => isWithin(location.pathname, navPath))
      .reduce((longest, navPath) => (navPath.length > longest.length ? navPath : longest), '');
    return deepestMatch === path;
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-50 flex h-full w-64 flex-col bg-white shadow-sm transition-transform duration-300 lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Logo area */}
        <div className="flex h-16 items-center justify-between border-b border-slate-100 px-6">
          <div className="flex items-center gap-2">
            {/* TODO: Replace with actual ConAI logo */}
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-600">
              <span className="text-sm font-bold text-white">C</span>
            </div>
            <span className="text-lg font-bold text-slate-900">ConAI Admin</span>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 lg:hidden"
            aria-label="Close sidebar"
          >
            <XIcon size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav 
          className="flex-1 overflow-y-auto px-3 py-4"
          style={{ overscrollBehavior: 'contain' }}
        >
          {/* Catalog Navigation */}
          <div className="space-y-1">
            <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
              {t('navCatalog')}
            </p>
            {mainNavItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  isActiveRoute(item.path)
                    ? 'bg-primary-50 text-primary-600'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                )}
              >
                <item.icon
                  size={20}
                  className={cn(
                    isActiveRoute(item.path) ? 'text-primary-600' : 'text-slate-400'
                  )}
                />
                {navLabel(item)}
              </NavLink>
            ))}
          </div>

          {/* Inventory Navigation */}
          <div className="mt-6 space-y-1">
            <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
              {t('navInventory')}
            </p>
            {inventoryNavItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  isActiveRoute(item.path)
                    ? 'bg-primary-50 text-primary-600'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                )}
              >
                <item.icon
                  size={20}
                  className={cn(
                    isActiveRoute(item.path) ? 'text-primary-600' : 'text-slate-400'
                  )}
                />
                {navLabel(item)}
              </NavLink>
            ))}
          </div>

          {/* Sales Navigation */}
          <div className="mt-6 space-y-1">
            <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
              {t('navSales')}
            </p>
            {salesNavItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  isActiveRoute(item.path)
                    ? 'bg-primary-50 text-primary-600'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                )}
              >
                <item.icon
                  size={20}
                  className={cn(
                    isActiveRoute(item.path) ? 'text-primary-600' : 'text-slate-400'
                  )}
                />
                {navLabel(item)}
              </NavLink>
            ))}
          </div>

          {/* Customer Navigation */}
          <div className="mt-6 space-y-1">
            <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
              {t('navCustomers')}
            </p>
            {customerNavItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  isActiveRoute(item.path)
                    ? 'bg-primary-50 text-primary-600'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                )}
              >
                <item.icon
                  size={20}
                  className={cn(
                    isActiveRoute(item.path) ? 'text-primary-600' : 'text-slate-400'
                  )}
                />
                {navLabel(item)}
              </NavLink>
            ))}
          </div>

          {/* Management Navigation */}
          <div className="mt-6 space-y-1">
            <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
              {t('navManagement')}
            </p>
            {managementNavItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  isActiveRoute(item.path)
                    ? 'bg-primary-50 text-primary-600'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                )}
              >
                <item.icon
                  size={20}
                  className={cn(
                    isActiveRoute(item.path) ? 'text-primary-600' : 'text-slate-400'
                  )}
                />
                {navLabel(item)}
              </NavLink>
            ))}
          </div>
        </nav>

        {/* Bottom section */}
        <div className="border-t border-slate-100 px-3 py-4">
          {bottomNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onClose}
              className={cn(
                'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                isActiveRoute(item.path)
                  ? 'bg-primary-50 text-primary-600'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              )}
            >
              <item.icon
                size={20}
                className={cn(
                  isActiveRoute(item.path) ? 'text-primary-600' : 'text-slate-400'
                )}
              />
              {navLabel(item)}
            </NavLink>
          ))}

          <button
            onClick={handleLogout}
            className="mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-red-50 hover:text-red-600"
          >
            <LogoutIcon size={20} className="text-slate-400" />
            {t('navLogout')}
          </button>
        </div>
      </aside>
    </>
  );
}
